import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { ArrowRight, Upload, X, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CreateAuction() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", deadline: "", currency: "INR" });
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [pdf, setPdf] = useState(null); // {storage_path, original_filename}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const addEmail = () => {
    const e = emailInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast.error("Invalid email");
      return;
    }
    if (emails.includes(e)) return;
    setEmails([...emails, e]);
    setEmailInput("");
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPdf(data);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (emails.length === 0) {
      setError("Add at least one bidder email.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        deadline: form.deadline || null,
        requirement_path: pdf?.storage_path || null,
        requirement_filename: pdf?.original_filename || null,
        invited_emails: emails,
        currency: form.currency,
      };
      const { data } = await api.post("/auctions", payload);
      toast.success("Auction created");
      navigate(`/auctions/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="create-auction-page">
      <TopBar />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2">New Auction</div>
        <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tightest leading-none mb-10">
          Define your requirement
        </h1>

        <form onSubmit={onSubmit} className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                placeholder="e.g. Office Furniture — 200 Workstations"
                className="rounded-none border-black/20 h-11"
                data-testid="auction-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                required
                rows={6}
                placeholder="Scope, specs, timelines..."
                className="rounded-none border-black/20"
                data-testid="auction-desc-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Deadline (optional)</Label>
              <Input
                type="datetime-local"
                value={form.deadline}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                className="rounded-none border-black/20 h-11"
                data-testid="auction-deadline-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger className="rounded-none border-black/20 h-11" data-testid="auction-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="INR" data-testid="currency-option-inr">INR — Indian Rupee (₹)</SelectItem>
                  <SelectItem value="USD" data-testid="currency-option-usd">USD — US Dollar ($)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-neutral-500">All bids and targets for this auction will use this currency. It cannot be changed after the auction is created.</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Requirement Document (PDF)</Label>
              {!pdf ? (
                <label
                  className="border-2 border-dashed border-black/20 p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:border-black transition-colors"
                  data-testid="upload-pdf-zone"
                >
                  <Upload size={28} weight="bold" />
                  <div className="mt-3 text-sm font-medium">{uploading ? "Uploading..." : "Drop PDF or click to upload"}</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mt-1">Optional</div>
                  <input type="file" accept="application/pdf,image/*" hidden onChange={onUpload} disabled={uploading} data-testid="upload-pdf-input" />
                </label>
              ) : (
                <div className="border border-black/15 p-4 flex items-center gap-3" data-testid="uploaded-pdf">
                  <div className="w-10 h-10 bg-[#0B1929] text-white flex items-center justify-center"><FileText size={18} weight="bold" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{pdf.original_filename}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Uploaded</div>
                  </div>
                  <button type="button" onClick={() => setPdf(null)} className="p-2 hover:bg-neutral-100" data-testid="remove-pdf">
                    <X size={16} weight="bold" />
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Invite Bidders (by email)</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                  placeholder="vendor@company.com"
                  className="rounded-none border-black/20 h-11"
                  data-testid="invite-email-input"
                />
                <Button type="button" onClick={addEmail} className="rounded-none bg-[#0B1929] hover:bg-[#132639] text-white h-11 px-5" data-testid="invite-email-add">
                  Add
                </Button>
              </div>
              {emails.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2" data-testid="invited-list">
                  {emails.map((e) => (
                    <span key={e} className="inline-flex items-center gap-2 border border-black/20 px-2.5 py-1 text-xs font-mono-plex">
                      {e}
                      <button type="button" onClick={() => setEmails(emails.filter((x) => x !== e))}>
                        <X size={12} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {error && <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-3" data-testid="create-auction-error">{error}</div>}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-12 btn-sharp"
              data-testid="create-auction-submit"
            >
              {submitting ? "Creating..." : "Launch Auction"}
              <ArrowRight size={14} weight="bold" className="ml-2" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
